import 'CssFolder/photo-input.scss';
import * as bootstrap from 'bootstrap';
import languageModel from '../../models/language.model';

let instance = 0;

const FILE_UPLOAD_MIME_TYPES = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.gif': 'image/gif',
    '.png': 'image/png'
};

/**
 * Photo Input Component
 * 
 * Photo input component that only accept images type formats.
 * Will display list of uploaded images in a carousel.
 * 
 * VULNERABILITY NOTE: Does not validate File Signatures or Double File Extensions
 * 
 * @author Darrus Goh
 */
export default function PhotoInputComponent() {
    const DEFAULT_ID = `PhotoInputComponent_${instance++}`;

    const DROP_EFFECT_NONE = 'none';
    const DROP_EFFECT_COPY = 'copy';
    const SUPPORTED_FILE_FORMATS_TEXT = Object.values(FILE_UPLOAD_MIME_TYPES).join(', ');

    const DEFAULT_ONINPUT = (files) => { console.log('FileUploadInputComponent: onInput(files | file)') };

    let carousel; // Bootstrap carousel instance
    let dataTransfer; // To hold the `FileList` of `file:input`
    let fileInputEl;
    let images = [];
    let validationMessage = '';
    let dropEffect = DROP_EFFECT_COPY;

    const handleOnChange = (onInput, photoCountLimit, filesizeLimit) => (e) => {
        handleUpload(e.target.files, onInput, photoCountLimit, filesizeLimit);
    };

    const handleUpload = (files, onInput, photoCountLimit, filesizeLimit) => {
        if (Array.from(files).length == 0) {
            return;
        }

        let tempFiles = structuredClone(images);
        tempFiles = tempFiles.concat(Array.from(files));

        validationMessage = validateFiles(tempFiles, photoCountLimit, filesizeLimit);
        if (validationMessage === '') {
            // Generate object url here so that it doesn't keep regenerating every redraw
            tempFiles.forEach(image => {
                if (!image.hasOwnProperty('src')) {
                    image.src = URL.createObjectURL(image);
                }
            });
            images = tempFiles;
            onInput(photoCountLimit > 1 ? tempFiles : tempFiles[0]);

            // Add newly selected files to `dataTransfer`
            for (const file of files) {
                dataTransfer.items.add(file);
            }
        }

        // Update `FileList` of `input:file` OR reset `input:file` if no change
        fileInputEl.files = dataTransfer.files;

        // DEBUG
        console.log('fileInputEl.files:', fileInputEl.files)
    };

    const validateFiles = (files, photoCountLimit, filesizeLimit) => {
        let consolidatedFilesize = 0;
        let validationMessage = '';

        validationMessage = validatePhotoCountLimit(files, photoCountLimit);
        if (validationMessage !== '') return validationMessage;

        for (const file of files) {
            consolidatedFilesize += file.size;

            let extensionValidation = validateExtension(file);
            if (extensionValidation !== '') return extensionValidation;
        }

        return validateTotalFilesize(consolidatedFilesize, filesizeLimit);
    };

    const validatePhotoCountLimit = (files, photoCountLimit) => {
        return files.length <= photoCountLimit ? '' : languageModel.getText('EM00000791', 'Number of selected files is more than allowed.')
    };

    const validateExtension = (file) => {
        const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
        return file.type === FILE_UPLOAD_MIME_TYPES[extension] ? '' : languageModel.getText('EM00000058', 'Invalid file.');
    };

    const validateTotalFilesize = (consolidatedFilesize, filesizeLimit) => {
        return consolidatedFilesize < filesizeLimit ? '' : `${languageModel.getText('EM00000789', 'Total size of the files should be less than')} ${filesizeLimit << 20}MB`;
    }

    const clearImages = (onInput, photoCountLimit, id) => {
        images = [];
        onInput(photoCountLimit > 1 ? images : images.at(0));

        // Remove items from `dataTransfer` and update to `file:input`
        dataTransfer.items.clear();
        fileInputEl.files = dataTransfer.files

        // DEBUG
        console.log('fileInputEl.files:', fileInputEl.files)
    };

    const deleteImage = (onInput, photoCountLimit, id) => {
        const carouselEl = document.getElementById(`${id}-carousel`);
        const activeCarouselItem = carouselEl.querySelector('.carousel .carousel-item.active');
        const activeItemIndex = activeCarouselItem.dataset.index;

        // Remove item from `dataTransfer` and update to `file:input`
        dataTransfer.items.remove(activeItemIndex);
        fileInputEl.files = dataTransfer.files

        // Remove item
        images.splice(activeItemIndex, 1);

        // Move to previous slide
        carousel.prev();

        onInput(photoCountLimit > 1 ? images : images.at(0));

        // DEBUG
        console.log('fileInputEl.files:', fileInputEl.files)
    }

    const clickInput = (id) => {
        document.getElementById(id).click();
    };

    const onDrop = (onInput, photoCountLimit, filesizeLimit) => (e) => {
        e.preventDefault();
        e.target.classList.remove('dragover');
        handleUpload(e.dataTransfer.files, onInput, photoCountLimit, filesizeLimit);
    };

    const onDragEnter = (photoCountLimit) => (e) => {
        e.preventDefault();

        if (e.dataTransfer.items) {
            if (e.dataTransfer.items.length > photoCountLimit) {
                dropEffect = DROP_EFFECT_NONE;
                return;
            }

            for (const item of e.dataTransfer.items) {
                if (!Object.values(FILE_UPLOAD_MIME_TYPES).find(mimetype => mimetype === item.type)) {
                    dropEffect = DROP_EFFECT_NONE;
                    return;
                }
            }

            dropEffect = DROP_EFFECT_COPY;
            e.target.classList.add('dragover');
        }
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = dropEffect;
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.target.classList.remove('dragover');
    };

    const initializeFileInput = (id) => {
        fileInputEl = document.getElementById(`${id}`);
        dataTransfer = new DataTransfer();
    }


    const initializeCarousel = ({ attrs: { id } }) => {
        carousel = new bootstrap.Carousel(`#${CSS.escape(id)}`);
    }

    return {
        oncreate: ({ attrs: { id = DEFAULT_ID } }) => {
            initializeFileInput(id);
        },
        view: ({ attrs: {
            id = DEFAULT_ID,
            required = false,
            photoCountLimit = 1,
            filesizeLimit = 10 << 20, // 10MB
            onInput = DEFAULT_ONINPUT } }) => m(`.photo-input${required && images.length === 0 ? '.is-invalid-custom' : ''}`,
                m('input', {
                    id,
                    required,
                    multiple: photoCountLimit > 1,
                    onchange: handleOnChange(onInput, photoCountLimit, filesizeLimit),
                    type: 'file',
                    accept: SUPPORTED_FILE_FORMATS_TEXT,
                    capture: 'camera'
                }),
                images?.length !== 0
                    ? [
                        m('.carousel.carousel-dark.slide.mb-2', { oncreate: initializeCarousel, id: `${id}-carousel` },
                            m('.carousel-indicators',
                                Array.from(images).map((_, index) => [
                                    m('button', {
                                        key: _.src,
                                        class: index === 0 ? 'active' : '',
                                        type: 'button',
                                        'data-bs-target': `#${CSS.escape(id)}-carousel`,
                                        'data-bs-slide-to': index,
                                        'aria-label': `Image ${index + 1}`
                                    })
                                ])
                            ),
                            m('.carousel-inner',
                                Array.from(images).map((image, index) => {
                                    return m('.carousel-item', {
                                        key: image.src,
                                        class: index === 0 ? 'active' : '',
                                        'data-index': index,
                                    },
                                        m('img', { src: image.src }),
                                    );
                                })
                            ),
                            images.length > 1 && m('button.carousel-control-prev', { type: 'button', 'data-bs-target': `#${CSS.escape(id)}-carousel`, 'data-bs-slide': 'prev' },
                                m('span.carousel-control-prev-icon'),
                                m('.visually-hidden', 'Previous')
                            ),
                            images.length > 1 && m('button.carousel-control-next', { type: 'button', 'data-bs-target': `#${CSS.escape(id)}-carousel`, 'data-bs-slide': 'next' },
                                m('span.carousel-control-next-icon'),
                                m('.visually-hidden', 'Next')
                            ),
                        ),
                        m('.photo-input-button-group',
                            images.length < photoCountLimit && m('button.btn.btn-primary[type=button]', { onclick: () => { clickInput(id) } },
                                m('span', m('i.bi-plus-lg.me-2'), languageModel.getText('LT00000988', 'Add Image(s)'))
                            ),
                            m('button.btn.btn-danger[type=button]', { onclick: () => { deleteImage(onInput, photoCountLimit, id) } },
                                m('span', m('i.bi-x-lg.me-2'), languageModel.getText('LT00000989', 'Delete Image'))
                            ),
                            images.length > 1 && m('button.btn.btn-danger[type=button]', { onclick: () => { clearImages(onInput, photoCountLimit, id) } },
                                m('span', m('i.bi-trash.me-2'), languageModel.getText('LT00000981', 'Clear Images'))
                            )
                        )
                    ]
                    : m('.photo-input-drag-area', {
                        ondrop: onDrop(onInput, photoCountLimit, filesizeLimit),
                        onclick: () => { clickInput(id) },
                        ondragenter: onDragEnter(photoCountLimit),
                        ondragover: onDragOver,
                        ondragleave: onDragLeave,
                    },
                        m('i.bi-images'),
                        m('span', languageModel.getText('LT00000982', 'Choose an image file or drag here to upload')),
                        m('small', `${languageModel.getText('LT00000983', 'Supported File Types')}: ${SUPPORTED_FILE_FORMATS_TEXT}`),
                        m('small', `${languageModel.getText('LT00000984', 'Maximum File Size')}: ${filesizeLimit >> 20}MB`),
                        photoCountLimit > 1 && m('small', `${languageModel.getText('LT00000985', 'Maximum number of file(s)')}: ${photoCountLimit}`)
                    ),
                validationMessage !== '' && m('.photo-input-validation-text', validationMessage),
            )
    }
}